import { useState } from 'react'
import { Check, X, Crown, Zap, Building2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/contexts/AuthContext'
import { cn } from '@/lib/utils'

interface PlanFeature {
  name: string
  starter: boolean | string
  growth: boolean | string
  pro: boolean | string
}

const plans = [
  {
    id: 'starter',
    name: 'Business Starter',
    icon: Building2,
    monthlyPrice: 1000,
    yearlyPrice: 10000,
    description: 'Perfect for freelancers and small businesses just getting started',
    popular: false,
    color: 'from-slate-500 to-slate-600',
  },
  {
    id: 'growth',
    name: 'Business Growth',
    icon: Zap,
    monthlyPrice: 2000,
    yearlyPrice: 18000,
    description: 'Ideal for growing teams that need collaboration features',
    popular: true,
    color: 'from-blue-500 to-blue-600',
  },
  {
    id: 'pro',
    name: 'Business PRO',
    icon: Crown,
    monthlyPrice: 5000,
    yearlyPrice: 45000,
    description: 'For established agencies requiring advanced features and unlimited access',
    popular: false,
    color: 'from-purple-500 to-purple-600',
  },
]

const features: PlanFeature[] = [
  { name: 'Unlimited clients', starter: true, growth: true, pro: true },
  { name: 'Unlimited invoices & estimates', starter: true, growth: true, pro: true },
  { name: 'Expense tracking', starter: true, growth: true, pro: true },
  { name: 'Unlimited Projects & tasks', starter: true, growth: true, pro: true },
  { name: 'Time tracking (manual + timer)', starter: true, growth: true, pro: true },
  { name: 'Branded invoice templates', starter: true, growth: true, pro: true },
  { name: 'Email invoice delivery', starter: true, growth: true, pro: true },
  { name: 'Basic reports', starter: true, growth: true, pro: true },
  { name: 'Single workspace', starter: true, growth: true, pro: true },
  { name: 'Email support', starter: true, growth: true, pro: true },
  { name: 'Client portal', starter: true, growth: true, pro: true },
  { name: 'Recurring Invoices', starter: false, growth: true, pro: true },
  { name: 'Team collaboration on Projects', starter: false, growth: true, pro: true },
  { name: 'User Addition', starter: false, growth: 'Upto 5 Users', pro: 'Unlimited' },
  { name: 'AI bank reconciliation', starter: false, growth: 'Only 2 bank statements per month', pro: '20 bank statements per month' },
  { name: 'Contract management & e-sign', starter: false, growth: 'Upto 5 Contracts', pro: 'Unlimited' },
  { name: 'Multi-currency support', starter: false, growth: false, pro: true },
]

export default function SubscriptionPage() {
  const { currentTenant } = useAuth()
  const [isYearly, setIsYearly] = useState(true)

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  const getYearlySavings = (monthly: number, yearly: number) => {
    const yearlyCostIfMonthly = monthly * 12
    const savings = yearlyCostIfMonthly - yearly
    const percentSaved = Math.round((savings / yearlyCostIfMonthly) * 100)
    return { savings, percentSaved }
  }

  const renderFeatureValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="h-5 w-5 text-green-500" />
      ) : (
        <X className="h-5 w-5 text-gray-300 dark:text-gray-600" />
      )
    }
    return <span className="text-sm text-center">{value}</span>
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold tracking-tight">Choose Your Plan</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Select the perfect plan for your business. All plans include our core features to help you manage your agency efficiently.
        </p>
        
        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-4 pt-4">
          <Label htmlFor="billing-toggle" className={cn("text-sm", !isYearly && "font-semibold")}>
            Monthly
          </Label>
          <Switch
            id="billing-toggle"
            checked={isYearly}
            onCheckedChange={setIsYearly}
          />
          <Label htmlFor="billing-toggle" className={cn("text-sm", isYearly && "font-semibold")}>
            Yearly
            <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              Save up to 25%
            </Badge>
          </Label>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {plans.map((plan) => {
          const price = isYearly ? plan.yearlyPrice : plan.monthlyPrice
          const { savings, percentSaved } = getYearlySavings(plan.monthlyPrice, plan.yearlyPrice)
          const PlanIcon = plan.icon
          
          return (
            <Card
              key={plan.id}
              className={cn(
                "relative overflow-hidden transition-all duration-300 hover:shadow-xl",
                plan.popular && "ring-2 ring-blue-500 dark:ring-blue-400 scale-105"
              )}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-none rounded-bl-lg bg-blue-500 text-white">
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-2">
                <div className={cn(
                  "w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center bg-gradient-to-br",
                  plan.color
                )}>
                  <PlanIcon className="h-6 w-6 text-white" />
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription className="min-h-[40px]">{plan.description}</CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <div className="text-center">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold">{formatPrice(price)}</span>
                    <span className="text-muted-foreground">/{isYearly ? 'year' : 'month'}</span>
                  </div>
                  {isYearly && (
                    <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                      Save {formatPrice(savings)} ({percentSaved}% off)
                    </p>
                  )}
                  {!isYearly && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatPrice(plan.yearlyPrice)}/year if billed annually
                    </p>
                  )}
                </div>

                <Button
                  className={cn(
                    "w-full",
                    plan.popular
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                      : ""
                  )}
                  variant={plan.popular ? "default" : "outline"}
                >
                  {currentTenant?.subscription_tier === 'PREMIUM' ? 'Current Plan' : 'Get Started'}
                </Button>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Feature Comparison Table */}
      <div className="max-w-6xl mx-auto mt-16">
        <h2 className="text-2xl font-bold text-center mb-8">Compare All Features</h2>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-semibold min-w-[250px]">Feature</th>
                    <th className="text-center p-4 font-semibold min-w-[150px]">
                      <div className="flex flex-col items-center gap-1">
                        <Building2 className="h-5 w-5 text-slate-500" />
                        <span>Business Starter</span>
                      </div>
                    </th>
                    <th className="text-center p-4 font-semibold min-w-[150px] bg-blue-50 dark:bg-blue-950/30">
                      <div className="flex flex-col items-center gap-1">
                        <Zap className="h-5 w-5 text-blue-500" />
                        <span>Business Growth</span>
                        <Badge className="bg-blue-500 text-white text-xs">Popular</Badge>
                      </div>
                    </th>
                    <th className="text-center p-4 font-semibold min-w-[150px]">
                      <div className="flex flex-col items-center gap-1">
                        <Crown className="h-5 w-5 text-purple-500" />
                        <span>Business PRO</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((feature, index) => (
                    <tr
                      key={feature.name}
                      className={cn(
                        "border-b last:border-b-0",
                        index % 2 === 0 ? "bg-background" : "bg-muted/30"
                      )}
                    >
                      <td className="p-4 font-medium">{feature.name}</td>
                      <td className="p-4 text-center">
                        <div className="flex justify-center">
                          {renderFeatureValue(feature.starter)}
                        </div>
                      </td>
                      <td className="p-4 text-center bg-blue-50/50 dark:bg-blue-950/20">
                        <div className="flex justify-center">
                          {renderFeatureValue(feature.growth)}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        <div className="flex justify-center">
                          {renderFeatureValue(feature.pro)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CTA Section */}
      <div className="text-center py-12 space-y-4">
        <h3 className="text-xl font-semibold">Need help choosing the right plan?</h3>
        <p className="text-muted-foreground">
          Contact our sales team for a personalized recommendation based on your business needs.
        </p>
        <Button variant="outline" size="lg">
          Contact Sales
        </Button>
      </div>
    </div>
  )
}

